import React from 'react';
import { Catalog, Document } from '@carbon/icons-react';
import {
  Button,
  Dialog,
  DialogContentContainer,
  TextField,
} from '@ditioas/web-ui';
import { useTranslation } from 'react-i18next';

import { useGetReleaseNotes } from 'data/menu';
import { parseAndFormatDateTime } from 'libs/dateFunctions';

interface ReleaseNotesDialogProps {
  /** Compact muted trigger for the modernized TopBarV2 */
  v2?: boolean;
}

const ReleaseNotesDialog = ({ v2 = false }: ReleaseNotesDialogProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const { data: releaseNotes } = useGetReleaseNotes(!open);

  const filteredReleaseNotes = React.useMemo(() => {
    if (!releaseNotes) return [];

    if (!search.trim()) return releaseNotes;

    return releaseNotes.filter((note) => {
      const query = search.toLowerCase();

      return (
        note.text?.toLowerCase().includes(query) ||
        note.versionString?.toLowerCase().includes(query)
      );
    });
  }, [releaseNotes, search]);

  React.useEffect(() => {
    if (open) {
      setSearch('');
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title={t('General.ReleaseNotes')}
      trigger={
        v2 ? (
          <Button
            variant="clear"
            size="sm"
            leftIcon={<Document />}
            className="text-sand-900 hover:text-sand-1200 shrink-0 gap-1.5"
            data-testid="layout.release-notes"
          >
            <p className="body-xs">{t('General.ReleaseNotes')}</p>
          </Button>
        ) : (
          <Button
            variant="clear"
            size="sm"
            className="font-normal"
            data-testid="layout.release-notes"
          >
            <Catalog className="shrink-0" />
            {t('General.ReleaseNotes')}
          </Button>
        )
      }
      className="min-h-1/2 w-3xl"
    >
      <DialogContentContainer className="relative grid grid-cols-[auto_auto_1fr]">
        <TextField
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('General.Search')}
          // className="body-xs border-sand-400 mb-3 w-full rounded border px-3 py-2 outline-none focus:border-black"
          containerProps={{
            className: 'sticky top-0 bg-white pb-2 z-10 col-span-3',
          }}
        />
        {filteredReleaseNotes.map((note) => (
          <React.Fragment
            key={note.id} /* className="flex items-start gap-4" */
          >
            <p className="label-xs pt-3">{note.versionString}</p>
            <p className="body-xxs pt-3">
              {parseAndFormatDateTime({
                value: note.releaseDateTime,
                parseAsLocalDate: true,
              })}
            </p>
            <p className="bg-sand-300 body-xs grow px-3 py-2.5 whitespace-pre-wrap">
              {note.text}
            </p>
          </React.Fragment>
        ))}
      </DialogContentContainer>
    </Dialog>
  );
};

export default ReleaseNotesDialog;
